import React, { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { useIntl } from 'react-intl';
import Stepper from '../shared/Stepper';
import WeekGridAssign, { WeekGridAssignment } from '../shared/WeekGridAssign';

interface TrainingCycleTemplateDialogProps {
  visible: boolean;
  onHide: () => void;
  templateName: string;
  setTemplateName: (name: string) => void;
  templateDuration: number | null;
  selectedDay: number | null;
  setSelectedDay: (day: number | null) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedWorkout: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSelectedWorkout: (workout: any) => void;
  isDurationInWeeks: boolean;
  applyToAllWeeks: boolean;
  setApplyToAllWeeks: (apply: boolean) => void;
  isReadOnly: boolean;
  setIsReadOnly: (readOnly: boolean) => void;
  selectedCycleId: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  templateWeeks: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleDurationTypeChange: (e: any) => void;
  handleRemoveWorkoutFromWeek: (weekIndex: number, sessionIndex: number) => void;
  handleAddWorkout: (workoutId: number, dayNumber: number, applyToAll: boolean, weekIndex?: number) => void;
  handleCreateTemplate: () => void;
  handleDurationChange: (duration: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workouts: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deletedWorkoutTemplates: any[];
}

const DURATION_CHIPS = [4, 8, 12, 16] as const;

/**
 * v2 cycle template wizard (design handoff §4.3).
 *
 * Two-step linear wizard:
 *   Step 1 — name + duration chips (no dates, because the template is reusable)
 *   Step 2 — assign workouts per week
 *
 * When editing an existing template we still respect `isReadOnly`. The
 * existing accordion-of-weeks UI for week-level assignment is kept (the
 * drag&drop day-grid §4.3 step 2 is a follow-up polish).
 */
export function TrainingCycleTemplateDialog({
  visible,
  onHide,
  templateName,
  setTemplateName,
  templateDuration,
  isDurationInWeeks,
  applyToAllWeeks,
  setApplyToAllWeeks,
  isReadOnly,
  setIsReadOnly,
  selectedCycleId,
  templateWeeks,
  handleDurationTypeChange,
  handleRemoveWorkoutFromWeek,
  handleAddWorkout,
  handleCreateTemplate,
  handleDurationChange,
  workouts,
  deletedWorkoutTemplates
}: TrainingCycleTemplateDialogProps) {
  const intl = useIntl();
  const [step, setStep] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(1);

  React.useEffect(() => {
    if (visible) {
      // #39: "Ver" un ciclo abre directamente la grilla de la semana. El paso de
      // "Datos" es del flujo de creación/edición; en lectura no aporta nada y era
      // justamente lo que hacía que "Ver" pareciera la pantalla de edición.
      setStep(isReadOnly ? 1 : 0);
      setSelectedWeek(1);
    }
  }, [visible, isReadOnly]);

  // Map current-week sessions to WeekGridAssign's flat shape. Each session has
  // dayNumber + workout (or workout.id). We resolve workoutId so the grid can
  // look up the workout name from `workouts`.
  const currentWeekIndex = selectedWeek - 1;
  const gridAssignments: WeekGridAssignment[] = useMemo(() => {
    const week = templateWeeks?.[currentWeekIndex];
    if (!week) return [];
    return (week.trainingSessions ?? [])
      .map((s: any) => {
        const workoutId = s.workout?.id ?? s.workout;
        const dayOfWeek = s.dayNumber;
        if (workoutId == null || dayOfWeek == null) return null;
        return { workoutId: Number(workoutId), dayOfWeek: Number(dayOfWeek) };
      })
      .filter(Boolean) as WeekGridAssignment[];
  }, [templateWeeks, currentWeekIndex]);

  const handleGridAssign = (dayOfWeek: number, workoutId: number) => {
    if (isReadOnly) return;
    handleAddWorkout(workoutId, dayOfWeek, applyToAllWeeks, currentWeekIndex);
  };

  const handleGridUnassign = (dayOfWeek: number) => {
    if (isReadOnly) return;
    const week = templateWeeks?.[currentWeekIndex];
    if (!week) return;
    const idx = (week.trainingSessions ?? []).findIndex((s: any) => Number(s.dayNumber) === dayOfWeek);
    if (idx < 0) return;
    handleRemoveWorkoutFromWeek(currentWeekIndex, idx);
  };

  const steps = useMemo(
    () => [
      { key: 'data', label: intl.formatMessage({ id: 'cycle.wizard.step.data' }) },
      { key: 'assign', label: intl.formatMessage({ id: 'cycle.wizard.step.assign' }) }
    ],
    [intl]
  );

  const getHeader = () => {
    if (!selectedCycleId) {
      return intl.formatMessage({ id: 'plansPage.createCycleDialog.header' });
    }
    return isReadOnly
      ? intl.formatMessage({ id: 'plansPage.viewCycleDialog.header' })
      : intl.formatMessage({ id: 'plansPage.editCycleDialog.header' });
  };

  const renderStepData = () => (
    <div className="et-cycle-wizard__step">
      <div
        className="p-3"
        style={{
          background: 'var(--et-primary-soft)',
          borderRadius: 'var(--ios-radius-md)',
          fontSize: '13px',
          color: 'var(--et-primary-ink)'
        }}
      >
        <i className="pi pi-info-circle mr-2" />
        {intl.formatMessage({
          id: 'cycle.template.banner',
          defaultMessage:
            'Las plantillas son reutilizables. No tienen fecha de inicio — eso se elige cuando asignás la plantilla a un cliente.'
        })}
      </div>

      <div className="p-field">
        <label>
          {intl.formatMessage({ id: 'plansPage.templateNamePlaceholder', defaultMessage: 'Nombre de la plantilla' })}
        </label>
        <InputText
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder={intl.formatMessage({ id: 'plansPage.templateNamePlaceholder' })}
          className="w-full"
          disabled={isReadOnly}
        />
      </div>

      <div className="p-field">
        <label>{intl.formatMessage({ id: 'createCycle.durationInWeeks' })}</label>
        <div className="et-cycle-wizard__chips">
          {DURATION_CHIPS.map((w) => {
            const active = isDurationInWeeks && templateDuration === w;
            return (
              <button
                key={w}
                type="button"
                onClick={() => {
                  // Make sure duration unit is "weeks" before applying chip value.
                  if (!isDurationInWeeks) {
                    handleDurationTypeChange({
                      target: { value: true },
                      checked: true
                    } as unknown as React.ChangeEvent<HTMLInputElement>);
                  }
                  handleDurationChange(w);
                }}
                className={`et-cycle-wizard__chip${active ? ' is-active' : ''}`}
                disabled={isReadOnly}
              >
                {intl.formatMessage({ id: 'cycle.weekGrid.weekN' }, { n: w })}
              </button>
            );
          })}
          <InputNumber
            value={
              isDurationInWeeks && templateDuration && !DURATION_CHIPS.includes(templateDuration as 4 | 8 | 12 | 16)
                ? templateDuration
                : null
            }
            onValueChange={(e) => handleDurationChange(e.value || 0)}
            placeholder={intl.formatMessage({ id: 'common.custom', defaultMessage: 'Personalizada' })}
            min={1}
            max={52}
            mode="decimal"
            className="et-cycle-wizard__chip-input"
            inputStyle={{ width: '90px', textAlign: 'center' }}
            disabled={isReadOnly}
          />
        </div>
      </div>
    </div>
  );

  // Merge deleted templates into the lookup so historical sessions still
  // resolve a name in read-only mode.
  const allWorkouts = useMemo(
    () => [...(workouts ?? []), ...(deletedWorkoutTemplates ?? [])].map((w) => ({ id: w.id, planName: w.planName })),
    [workouts, deletedWorkoutTemplates]
  );

  const renderStepAssign = () => (
    <div className="et-cycle-wizard__step">
      <WeekGridAssign
        workouts={allWorkouts}
        durationInWeeks={templateWeeks?.length || 1}
        startDate={null}
        assignments={gridAssignments}
        onAssign={handleGridAssign}
        onUnassign={handleGridUnassign}
        replicate={applyToAllWeeks}
        onReplicateChange={(next) => setApplyToAllWeeks(next)}
        selectedWeek={selectedWeek}
        onWeekChange={setSelectedWeek}
        readOnly={isReadOnly}
      />
    </div>
  );

  const isLast = step === steps.length - 1;
  const canContinue = step === 0 ? !!templateName && !!templateDuration : true;

  const renderFooter = () => (
    <div className="et-cycle-wizard__footer">
      {step > 0 ? (
        <Button
          label={intl.formatMessage({ id: 'common.back', defaultMessage: 'Atrás' })}
          icon="pi pi-arrow-left"
          className="p-button-text"
          onClick={() => setStep((s) => s - 1)}
        />
      ) : (
        <Button label={intl.formatMessage({ id: 'common.cancel' })} className="p-button-text" onClick={onHide} />
      )}
      {isLast ? (
        !isReadOnly && (
          <Button
            label={intl.formatMessage({ id: 'plansPage.saveTemplate' })}
            icon="pi pi-check"
            onClick={handleCreateTemplate}
            style={{
              background: 'var(--et-cta-bg)',
              borderColor: 'var(--et-cta-bg)',
              color: 'var(--et-cta-fg)'
            }}
          />
        )
      ) : (
        <Button
          label={intl.formatMessage({ id: 'common.continue', defaultMessage: 'Continuar' })}
          icon="pi pi-arrow-right"
          iconPos="right"
          onClick={() => setStep((s) => s + 1)}
          disabled={!canContinue}
        />
      )}
    </div>
  );

  return (
    <Dialog
      dismissableMask
      className="responsive-dialog"
      modal
      draggable={false}
      resizable={false}
      header={
        <div className="flex gap-2 align-items-center">
          <span>{getHeader()}</span>
          {isReadOnly && selectedCycleId ? (
            <Button
              icon="pi pi-pencil"
              className="p-button-text"
              tooltip={intl.formatMessage({ id: 'plansPage.enableEdit' })}
              onClick={() => setIsReadOnly(false)}
            />
          ) : !isReadOnly && selectedCycleId ? (
            <Button
              icon="pi pi-lock"
              className="p-button-text"
              tooltip={intl.formatMessage({ id: 'plansPage.disableEdit' })}
              onClick={() => setIsReadOnly(true)}
            />
          ) : null}
        </div>
      }
      visible={visible}
      style={{ width: step === 1 ? 'min(1140px, 96vw)' : 'min(720px, 95vw)' }}
      onHide={onHide}
      footer={isReadOnly ? null : renderFooter()}
    >
      {!isReadOnly && <Stepper steps={steps} current={step} onStepClick={(i) => setStep(i)} />}
      {step === 0 ? renderStepData() : renderStepAssign()}
    </Dialog>
  );
}
